import { useDeleteChatSessionMutation } from "@api/chat";
import { IconWithTooltip } from "@components/IconWithTooltip";
import { ModalVariant, useModal } from "@components/Modal";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/16/solid";
import { ChatSessionDTO } from "@onetutor/shared/dto";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";

interface ChatSessionCardProps {
  session: ChatSessionDTO;
  isCurrent: boolean;
  patchChatSession: ({ sessionId, courseId, body: { name } }) => void;
}

export function ChatSessionCard({
  session,
  isCurrent,
  patchChatSession,
}: ChatSessionCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newSessionName, setNewSessionName] = useState(session.name);
  const { courseId } = useParams();
  const [, setSearchParams] = useSearchParams();
  const { t } = useTranslation("chatPage");

  const { Modal: DeleteModal, openModal: openDeleteModal } = useModal();
  const [deleteChatSession] = useDeleteChatSessionMutation();

  const confirmDelete = () => {
    if (courseId) {
      deleteChatSession({
        sessionId: session.id,
        courseId: courseId,
      });
      if (isCurrent) {
        setSearchParams({}, { replace: true });
      }
    }
  };

  const openChatSession = () => {
    setSearchParams({ sessionId: session.id }, { replace: true });
  };

  return (
    <>
      <button
        type="button"
        key={session.id}
        onClick={openChatSession}
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            openChatSession();
          }
        }}
        className={`w-full cursor-pointer rounded-xl px-3 py-1 ${isCurrent ? " bg-base-300" : "hover:bg-base-100"}`}
      >
        <div className="flex items-center justify-between">
          {isEditing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                patchChatSession({
                  sessionId: session.id,
                  courseId: courseId,
                  body: {
                    name: newSessionName,
                  },
                });
                setIsEditing(false);
              }}
              className="form-control w-full"
            >
              <input
                type="text"
                value={newSessionName || ""}
                placeholder={t("chatSession.defaultNameSession")}
                onChange={(e) => {
                  e.stopPropagation();
                  setNewSessionName(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                minLength={1}
                maxLength={50}
                required
                className="input input-sm w-full"
              />
            </form>
          ) : (
            <p>
              {session.name && session.name.length > 50
                ? `${session.name.substring(0, 50)}...`
                : session.name || t("chatSession.defaultNameSession")}
            </p>
          )}
          {!isEditing && (
            <div className="flex items-center">
              <IconWithTooltip
                icon={<PencilSquareIcon className="h-5 w-5" />}
                tooltip={t("chatSession.renameSession")}
                handler={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
                className="btn btn-ghost btn-sm btn-circle"
                tooltipPosition="left"
              />
              <IconWithTooltip
                icon={<TrashIcon className="h-5 w-5" />}
                tooltip={t("chatSession.deleteSession")}
                handler={(e) => {
                  e.stopPropagation();
                  openDeleteModal();
                }}
                className="btn btn-ghost btn-sm btn-circle"
                tooltipPosition="left"
              />
            </div>
          )}
        </div>
      </button>

      <DeleteModal
        title={t("chatSession.deleteModalTitle")}
        confirmVariant={ModalVariant.DESTRUCTIVE}
        onConfirm={confirmDelete}
      >
        {t("chatSession.deleteModalContent")}
      </DeleteModal>
    </>
  );
}
